import {
	RiBracesLine,
	RiCheckLine,
	RiErrorWarningLine,
	RiFileCopyLine,
	RiJavaLine,
	RiJavascriptFill,
	RiPhpLine,
	RiTerminalLine,
} from '@remixicon/react';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, Suspense, use } from 'react';

import { Norrsken } from '@shared/docs/norrsken';
import { type ClipboardState, useClipboard } from '@shared/hooks/clipboard';

import {
	type ExampleSource,
	exampleSources,
	hasExample,
} from '@portal/pkg/examples';

import css from './styles.module.css';

import { GoLogo } from './GoLogo';
import { rememberLanguage, useLanguage } from './language';

const names: Record<string, string> = {
	sh: 'curl',
	go: 'Golang',
	js: 'JavaScript',
	java: 'Java',
};

const icons: Record<string, ReactNode> = {
	sh: <RiTerminalLine />,
	go: <GoLogo />,
	js: <RiJavascriptFill />,
	java: <RiJavaLine />,
	php: <RiPhpLine />,
};

const crossfade = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	exit: { opacity: 0 },
	transition: { duration: 0.15, ease: 'easeOut' },
} as const;

const copyIcons: Record<ClipboardState, ReactNode> = {
	idle: <RiFileCopyLine />,
	copied: <RiCheckLine />,
	failed: <RiErrorWarningLine />,
};

const CopyResetDelay = 2000 as const;

interface ExamplesProps {
	/** The page directory, collection included: `docs/keys`. */
	page: string;

	name: string;
}

export function Examples({ page, name }: ExamplesProps): ReactNode {
	if (!hasExample(page, name)) {
		return (
			<p className={css.notice}>
				{page} has no example named {name}.
			</p>
		);
	}

	return (
		<Suspense
			fallback={
				<div className={css.wrapper}>
					<div className={css.stage} />
				</div>
			}>
			<ExamplesContent page={page} name={name} />
		</Suspense>
	);
}

function ExamplesContent({ page, name }: ExamplesProps): ReactNode {
	const examples: ExampleSource[] = use(exampleSources(page, name));
	const selected = useLanguage(examples.map(({ language }) => language));
	const { state, copy } = useClipboard(CopyResetDelay);

	const current = examples.find(({ language }) => language === selected);

	return (
		<article className={css.wrapper}>
			<ul className={css.tabs}>
				{examples.map(({ language }) => (
					<li
						key={language}
						data-active={language === selected}
						onClick={() => rememberLanguage(language)}>
						{icons[language] ?? <RiBracesLine />}
						{names[language] ?? language}
					</li>
				))}

				<li
					className={css.copy}
					data-state={state}
					onClick={() => void copy(current?.source ?? '')}>
					{copyIcons[state]}
				</li>
			</ul>

			<section className={css.stage}>
				<AnimatePresence>
					<motion.div {...crossfade} key={selected}>
						<Norrsken content={current?.source ?? ''} language={selected} />
					</motion.div>
				</AnimatePresence>
			</section>
		</article>
	);
}
